import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, ArrowRight, CheckCircle2, Phone, Mail, MapPin, Sparkles, HeartHandshake } from 'lucide-react'
import { scrapedData } from '../data'
import ContactForm from '../components/ContactForm'
import SEO from '../components/SEO'
import { imageUrl } from '../lib/images'

export default function Home() {
  const { index, therapists } = scrapedData
  const [activeStory, setActiveStory] = useState(0)

  // 1.4 Hvad kan vi hjælpe dig med - de 15 specificerede punkter
  const helpAreas = [
    "Ryg & lænd",
    "Nakke & skuldre",
    "Hovedpine & migræne",
    "Bækkenproblemer",
    "Muskelspændinger",
    "Spædbørn & børn",
    "Favoritside & skævt kranie",
    "Uro & spændinger",
    "Led- og muskelsmerter",
    "Overgangsalder",
    "Knæproblemer",
    "Angst",
    "ADHD",
    "Depression",
    "Koncentrationsbesvær"
  ]

  // 1.6 Flere behandlingsmuligheder
  const additionalTreatments = [
    "Fysiurgisk massage",
    "Cupping",
    "Myofascial Release (MFR)",
    "Øreakupunktur/NADA",
    "Vejrtrækning",
    "Iltterapi"
  ]

  return (
    <div className="space-y-20 pb-16">
      <SEO
        title="Manuel helhedsbehandling i Viborg | Rørbækklinikken"
        description="På Rørbækklinikken kombinerer vi mange års erfaring med forskellige manuelle behandlingsformer og akupunktur. Vi tager udgangspunkt i dig og sammensætter behandlingen efter dine behov."
      />
      
      {/* 1.1 Hero Section / Øverste introduktion */}
      <section className="relative bg-[#F5F0E6] pt-12 pb-14 sm:py-16 lg:py-24 overflow-hidden border-b border-[#E6DFD3]">
        {/* Desktop: Baggrundsbillede i højre side med flydende gradient-overlay */}
        <div className="absolute top-0 right-0 bottom-0 w-7/12 pointer-events-none overflow-hidden hidden lg:block">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=92,format=auto/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/ra-rba-kklinikken-forside.png"
            srcSet="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=92,format=auto/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/ra-rba-kklinikken-forside.png 800w, https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=92,format=auto/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/ra-rba-kklinikken-forside.png 1200w, https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=92,format=auto/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/ra-rba-kklinikken-forside.png 1800w"
            sizes="58vw"
            alt="Rørbækklinikken - Gravene 28B Viborg"
            className="w-full h-full object-cover object-right"
            loading="eager"
          />
          {/* Flydende gradient fra beige baggrund over i billedet */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#F5F0E6] via-[#F5F0E6]/85 via-30% sm:via-20% to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Hero Text */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1E352F]/5 border border-[#1E352F]/10 text-[#1E352F] text-xs font-semibold tracking-wider uppercase backdrop-blur-xs">
                <span className="w-2 h-2 rounded-full bg-[#eebb53] animate-pulse" />
                Rørbækklinikken · Viborg
              </div>

              {/* Hovedoverskrift */}
              <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl font-bold text-[#1E352F] leading-tight tracking-tight">
                Manuel helhedsbehandling i Viborg
              </h1>

              {/* Underoverskrift */}
              <p className="font-serif text-lg sm:text-2xl text-[#1E352F] font-medium leading-snug">
                Behandling af børn og voksne – med blik for hele mennesket
              </p>

              {/* Introduktionstekst */}
              <p className="text-base sm:text-lg text-[#1E352F]/85 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-normal">
                På Rørbækklinikken kombinerer vi mange års erfaring med forskellige manuelle behandlingsformer og akupunktur. Vi tager udgangspunkt i dig og sammensætter behandlingen efter dine behov.
              </p>
              
              {/* Hero CTAs */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
                <a
                  href="https://roerbaekklinikken.planway.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#eebb53] hover:bg-[#dca63d] text-white text-center font-bold px-8 py-3.5 rounded-full text-base transition-colors shadow-sm flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  <Calendar className="w-5 h-5" />
                  Book tid online
                </a>
                <Link
                  to="/behandlinger"
                  className="bg-white/80 hover:bg-white text-[#1E352F] border border-[#1E352F]/20 text-center font-semibold px-8 py-3.5 rounded-full text-base transition-colors flex items-center justify-center gap-2 w-full sm:w-auto shadow-xs"
                >
                  Se alle behandlinger
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Nøglefordele tags */}
              <div className="pt-4 flex flex-wrap items-center justify-center lg:justify-start gap-y-2 gap-x-6 text-xs sm:text-sm text-[#1E352F]/80">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#eebb53]" />
                  <span>Mange års erfaring</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#eebb53]" />
                  <span>RAB-godkendte behandlere</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#eebb53]" />
                  <span>Gravene 28B, Viborg</span>
                </div>
              </div>
            </div>

            {/* Mobile / Tablet Image */}
            <div className="lg:hidden col-span-1 flex justify-center">
              <div className="relative w-full max-w-md rounded-2xl overflow-hidden border border-[#E6DFD3] shadow-md aspect-4/3">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=90,format=auto/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/ra-rba-kklinikken-forside.png"
                  alt="Rørbækklinikken - Gravene 28B Viborg"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 1.3 Ny sektion: Vi ser på hele mennesket */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl border border-[#E6DFD3] p-8 sm:p-12 lg:p-16 shadow-xs space-y-8">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E352F]/5 text-[#1E352F] text-xs font-semibold tracking-wider uppercase">
              <HeartHandshake className="w-3.5 h-3.5 text-[#eebb53]" />
              Vores filosofi
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1E352F] leading-tight">
              Vi ser på hele mennesket
            </h2>
            <div className="space-y-4 text-base sm:text-lg text-[#1E352F]/85 leading-relaxed">
              <p>
                Kroppen hænger sammen. Derfor ser vi ikke kun på det sted, hvor generne mærkes, men også på de sammenhænge i kroppen, der kan have betydning.
              </p>
              <p>
                På Rørbækklinikken arbejder vi med flere forskellige behandlingsformer og har hver vores specialer. Det betyder, at behandlingen kan tilpasses dig – og at vi kan kombinere forskellige teknikker, når det giver mening.
              </p>
            </div>
          </div>

          {/* Afsluttende fremhævet tekst */}
          <div className="p-6 sm:p-8 rounded-2xl bg-[#F5F0E6]/80 border-l-4 border-[#eebb53] text-[#1E352F]">
            <p className="font-serif text-xl sm:text-2xl lg:text-3xl font-bold text-[#1E352F] leading-snug">
              Ingen mennesker er ens – og derfor skal behandling heller ikke være det.
            </p>
          </div>
        </div>
      </section>

      {/* 1.4 Sektion: Hvad kan vi hjælpe dig med? */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E352F]/5 text-[#1E352F] text-xs font-semibold tracking-wider uppercase">
            Overblik over fokusområder
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1E352F]">
            Hvad kan vi hjælpe dig med?
          </h2>
          <p className="text-base text-[#1E352F]/75 max-w-2xl mx-auto leading-relaxed">
            Her er en række af de typiske udfordringer og gener, vi møder og behandler i klinikken:
          </p>
        </div>

        {/* Overskueligt, responsivt grid med de 15 punkter */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3.5 sm:gap-4">
          {helpAreas.map((area, idx) => (
            <div
              key={idx}
              className="bg-white rounded-xl border border-[#E6DFD3] p-4 sm:p-5 flex items-center gap-3 shadow-xs hover:border-[#eebb53] hover:shadow-sm transition-all"
            >
              <span className="w-2 h-2 rounded-full bg-[#eebb53] shrink-0" />
              <span className="text-sm sm:text-base font-semibold text-[#1E352F] leading-snug">
                {area}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* 1.5 Sektion: Mød dine behandlere på klinikken */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E352F]/5 text-[#1E352F] text-xs font-semibold tracking-wider uppercase">
            Teamet
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1E352F]">
            Mød dine behandlere på klinikken
          </h2>
          <p className="text-base text-[#1E352F]/75 max-w-2xl mx-auto leading-relaxed">
            Vi har hver vores faglige baggrund og specialer, men deler et fælles helhedssyn på kroppen og mennesket.
          </p>
        </div>

        {/* Tre ensartede behandlerkort på desktop / under hinanden på mobil */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {therapists.map((therapist) => (
            <div
              key={therapist.slug}
              className="bg-white rounded-3xl border border-[#E6DFD3] overflow-hidden flex flex-col justify-between shadow-xs hover:shadow-md transition-shadow"
            >
              <div>
                {/* Portrætbillede */}
                <div className="h-72 sm:h-80 w-full overflow-hidden bg-[#F5F0E6] relative">
                  <img
                    src={imageUrl(therapist.image, 'width=600,quality=85,format=auto')}
                    alt={therapist.name}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>

                {/* Indhold */}
                <div className="p-6 sm:p-8 space-y-4">
                  <div className="space-y-1">
                    <h3 className="font-serif text-2xl font-bold text-[#1E352F]">
                      {therapist.name}
                    </h3>
                    <p className="text-xs sm:text-sm font-semibold text-[#eebb53] tracking-wide">
                      {therapist.title}
                    </p>
                  </div>
                  
                  <p className="text-sm sm:text-base text-[#1E352F]/80 leading-relaxed">
                    {therapist.homeDescription}
                  </p>
                </div>
              </div>

              {/* Læs mere-knap der fører til individuel profil */}
              <div className="p-6 sm:p-8 pt-0">
                <Link
                  to={`/om-os/${therapist.slug}`}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#F5F0E6]/80 hover:bg-[#F5F0E6] text-[#1E352F] border border-[#E6DFD3] text-sm font-bold py-3 px-6 rounded-full transition-colors text-center"
                >
                  Læs mere
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 1.6 Sektion: Flere behandlingsmuligheder */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#F5F0E6]/50 rounded-3xl border border-[#E6DFD3] p-8 sm:p-12 lg:p-16 space-y-8 text-center">
          <div className="max-w-3xl mx-auto space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1E352F]">
              Flere behandlingsmuligheder
            </h2>
            <p className="font-serif text-xl sm:text-2xl text-[#1E352F] font-medium">
              Flere måder at arbejde med kroppen på
            </p>
            <p className="text-base text-[#1E352F]/80">
              Ud over vores hovedområder tilbyder vi blandt andet:
            </p>
          </div>

          {/* Behandlingsoversigt */}
          <div className="max-w-4xl mx-auto py-4">
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              {additionalTreatments.map((treatment, idx) => (
                <span
                  key={idx}
                  className="bg-white px-5 py-2.5 rounded-full text-sm sm:text-base font-semibold text-[#1E352F] border border-[#E6DFD3] shadow-xs"
                >
                  {treatment}
                </span>
              ))}
            </div>
          </div>

          {/* Knap til alle behandlinger */}
          <div className="pt-4">
            <Link
              to="/behandlinger"
              className="inline-flex items-center gap-2 bg-[#1E352F] hover:bg-[#2D4A43] text-[#F5F0E6] px-8 py-3.5 rounded-full text-sm sm:text-base font-bold uppercase tracking-wider transition-colors shadow-sm"
            >
              SE ALLE BEHANDLINGER
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Patient Stories / Gode erfaringer */}
      <section className="bg-white py-16 border-y border-[#E6DFD3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E352F]/5 text-[#1E352F] text-xs font-semibold tracking-wider uppercase">
              Klienthistorier
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1E352F]">
              Gode erfaringer fra vores klienter
            </h2>
            <p className="text-sm sm:text-base text-[#1E352F]/70 leading-relaxed">
              Læs personlige beretninger fra klienter, der har modtaget behandling hos os.
            </p>
          </div>

          {/* Story Selector Tabs */}
          <div className="flex flex-wrap justify-center gap-2 border-b border-[#E6DFD3] pb-6">
            {index.stories.map((story, idx) => (
              <button
                key={idx}
                onClick={() => setActiveStory(idx)}
                className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                  activeStory === idx
                    ? 'bg-[#1E352F] text-white shadow-xs'
                    : 'bg-[#FDFBF7] text-[#1E352F]/80 border border-[#E6DFD3] hover:bg-[#F5F0E6]'
                }`}
              >
                {story.title}
              </button>
            ))}
          </div>

          {/* Active Story */}
          <div className="bg-[#FDFBF7] rounded-2xl border border-[#E6DFD3] p-6 sm:p-10 shadow-xs max-w-4xl mx-auto">
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#E6DFD3] pb-4">
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#1E352F]">
                  {index.stories[activeStory].title}
                </h3>
                {index.stories[activeStory].author && (
                  <span className="text-xs sm:text-sm font-semibold text-[#eebb53] bg-[#eebb53]/10 px-3 py-1 rounded-full w-fit">
                    Af: {index.stories[activeStory].author}
                  </span>
                )}
              </div>

              <p className="text-sm sm:text-base text-[#1E352F]/85 leading-relaxed whitespace-pre-line italic">
                {index.stories[activeStory].content}
              </p>
            </div>
          </div>

          <div className="text-center pt-2">
            <Link
              to="/gode-erfaringer"
              className="inline-flex items-center gap-2 text-[#1E352F] hover:text-[#eebb53] font-semibold text-sm transition-colors"
            >
              Læs flere klienthistorier og erfaringer
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 1.7 Booking- og kontaktsektion nederst (bevares med korrekt adresse og telefon) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#1E352F] rounded-3xl text-[#F5F0E6] p-8 sm:p-12 lg:p-14 border border-[#2D4A43] shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Booking Info & Clinic Details */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[#F5F0E6] text-xs font-semibold tracking-wider uppercase">
                  Tidsbestilling
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white">
                  Klar til at få det bedre?
                </h2>
                <p className="text-sm sm:text-base text-[#F5F0E6]/80 leading-relaxed">
                  Bestil din tid direkte i vores online bookingsystem Planway. Det er nemt, hurtigt og giver dig overblik over ledige tider hos alle vores behandlere.
                </p>
              </div>

              {/* Booking Box */}
              <div className="bg-[#162723] rounded-2xl p-6 border border-[#2D4A43] space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-[#eebb53]/20 rounded-xl text-[#eebb53]">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg font-bold text-white">
                      Planway Online Booking
                    </h3>
                    <p className="text-xs text-[#F5F0E6]/70">
                      Vælg behandling og behandler
                    </p>
                  </div>
                </div>

                <a
                  href="https://roerbaekklinikken.planway.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-[#eebb53] hover:bg-[#dca63d] text-white text-center font-bold py-3.5 px-6 rounded-full text-sm sm:text-base transition-colors shadow-sm flex items-center justify-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  Book tid online nu
                </a>
              </div>

              {/* Clinic Info Details */}
              <div className="space-y-3 pt-2 text-sm text-[#F5F0E6]/80 border-t border-[#2D4A43] pt-6">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-[#eebb53] shrink-0" />
                  <span>Gravene 28B, 8800 Viborg</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#eebb53] shrink-0" />
                  <span>
                    Tlf: <a href="tel:86639393" className="hover:underline">86 63 93 93</a>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#eebb53] shrink-0" />
                  <span>
                    E-mail: <a href="mailto:im@muskelterapi.com" className="hover:underline">im@muskelterapi.com</a>
                  </span>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7 bg-white text-[#1E352F] rounded-2xl p-8 sm:p-10 shadow-md border border-[#E6DFD3]">
              <div className="space-y-3 mb-6">
                <h3 className="font-serif text-2xl font-bold text-[#1E352F]">
                  Send os en besked
                </h3>
                <p className="text-sm text-[#1E352F]/70">
                  Har du spørgsmål til en behandling eller forløb, er du altid velkommen til at kontakte os via formularen herunder.
                </p>
              </div>

              <ContactForm />
            </div>

          </div>
        </div>
      </section>

    </div>
  )
}
